import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Brain, PenLine, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const STEPS = [
  {
    id: 'close',
    duration: 60,
    icon: Eye,
    title: 'CLOSE',
    instruction: "Close your eyes. Let the practice settle. Don't summarise - just let it be present.",
    canSkip: false
  },
  {
    id: 'recall',
    duration: 30,
    icon: Brain,
    title: 'RECALL',
    instruction: "Without looking at the instructions - what is the one thing you will carry from this session?",
    canSkip: false
  },
  {
    id: 'write',
    duration: 30,
    icon: PenLine,
    title: 'WRITE',
    instruction: "Write it. One sentence.",
    requiresInput: true,
    canSkip: false
  }
];

export const MemoryConsolidationClose = ({ onComplete, protocolName = 'Protocol' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [timeLeft, setTimeLeft] = useState(STEPS[0].duration);
  const [carryForwardNote, setCarryForwardNote] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  const step = STEPS[currentStep];

  // Timer countdown
  useEffect(() => {
    if (isComplete) return;
    if (step.requiresInput) return; // Don't auto-advance on write step
    
    if (timeLeft <= 0) {
      // Auto-advance to next step
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(prev => prev + 1);
        setTimeLeft(STEPS[currentStep + 1].duration);
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, currentStep, step.requiresInput, isComplete]);

  const handleSubmitNote = useCallback(() => {
    if (!carryForwardNote.trim()) return;
    setIsComplete(true);
    
    // Call onComplete with the carry-forward note
    if (onComplete) {
      onComplete({
        carryForwardNote: carryForwardNote.trim(),
        completedAt: new Date().toISOString()
      });
    }
  }, [carryForwardNote, onComplete]);

  // Calculate progress for the ring
  const progress = step.requiresInput ? 100 : ((step.duration - timeLeft) / step.duration) * 100;

  if (isComplete) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-secondary/20 flex items-center justify-center">
          <Check className="w-10 h-10 text-brand-secondary" />
        </div>
        <h3 className="font-heading text-2xl font-bold text-white mb-2">
          Memory Consolidated
        </h3>
        <p className="text-slate-400 mb-4">
          Your carry-forward note has been saved.
        </p>
        <div className="glass rounded-lg p-4 max-w-md mx-auto">
          <p className="text-sm text-slate-500 uppercase tracking-wider mb-2">Your Note</p>
          <p className="text-white italic">"{carryForwardNote}"</p>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="py-6">
      {/* Header */}
      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-widest text-amber-500 mb-2">Memory Consolidation</p>
        <h3 className="font-heading text-xl font-bold text-white">
          Let Your Practice Settle
        </h3>
      </div>

      {/* Step Progress */}
      <div className="flex justify-center gap-4 mb-8">
        {STEPS.map((s, index) => (
          <div 
            key={s.id}
            className={`flex items-center gap-2 ${index === currentStep ? 'text-amber-500' : index < currentStep ? 'text-brand-secondary' : 'text-slate-600'}`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
              index === currentStep ? 'border-amber-500 bg-amber-500/10' : 
              index < currentStep ? 'border-brand-secondary bg-brand-secondary/10' : 
              'border-slate-600'
            }`}>
              {index < currentStep ? (
                <Check className="w-4 h-4" />
              ) : (
                <span className="text-sm font-bold">{index + 1}</span>
              )}
            </div>
            <span className="text-sm font-medium hidden sm:inline">{s.title}</span>
          </div>
        ))}
      </div>

      {/* Current Step */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="text-center"
        >
          {/* Pulsing Ring Timer */}
          {!step.requiresInput && (
            <div className="relative w-32 h-32 mx-auto mb-6">
              {/* Background ring */}
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="58"
                  fill="none"
                  stroke="#1e293b"
                  strokeWidth="8"
                />
                {/* Progress ring */}
                <circle
                  cx="64"
                  cy="64"
                  r="58"
                  fill="none"
                  stroke="#C8860A"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 58}`}
                  strokeDashoffset={`${2 * Math.PI * 58 * (1 - progress / 100)}`}
                  className="transition-all duration-1000 ease-linear"
                  style={{
                    filter: 'drop-shadow(0 0 8px rgba(200, 134, 10, 0.5))'
                  }}
                />
              </svg>
              {/* Timer text */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-mono text-white">{timeLeft}s</span>
              </div>
              {/* Pulse effect */}
              <div 
                className="absolute inset-0 rounded-full border-2 border-amber-500/30 animate-ping"
                style={{ animationDuration: '2s' }}
              />
            </div>
          )}

          {/* Step Icon */}
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
            step.requiresInput ? 'bg-amber-500/20' : 'bg-slate-800'
          }`}>
            <step.icon className={`w-8 h-8 ${step.requiresInput ? 'text-amber-500' : 'text-white'}`} />
          </div>

          {/* Step Title */}
          <h4 className="font-mono text-lg uppercase tracking-widest text-amber-500 mb-4">
            {step.title}
          </h4>

          {/* Instruction */}
          <p className="text-lg text-slate-300 max-w-md mx-auto mb-6 leading-relaxed">
            {step.instruction}
          </p>

          {/* Write Input */}
          {step.requiresInput && (
            <div className="max-w-md mx-auto">
              <Input
                type="text"
                value={carryForwardNote}
                onChange={(e) => setCarryForwardNote(e.target.value)}
                placeholder="One sentence..."
                className="bg-slate-800 border-amber-500/30 text-white text-center text-lg py-4 mb-4"
                autoFocus
                data-testid="carry-forward-input"
              />
              <Button
                onClick={handleSubmitNote}
                disabled={!carryForwardNote.trim()}
                className="bg-amber-500 text-slate-900 hover:bg-amber-400 font-bold px-8 py-3"
                data-testid="submit-note-btn"
              >
                Complete Session
              </Button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default MemoryConsolidationClose;
